import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { DecisionLogTable } from "@/components/reports/DecisionLogTable";
import { PipelineReportTable } from "@/components/reports/PipelineReportTable";
import { PlanRejectionsTable } from "@/components/reports/PlanRejectionsTable";
import { PageContainer } from "@/components/shared/PageContainer";
import { PageHeader } from "@/components/shared/PageHeader";
import { ViewOnlyGate } from "@/components/shared/ViewOnlyGate";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { jsonFetch } from "@/lib/json-fetch";

type DriftRow = {
  tier: string;
  solutionsTotal: number;
  solutionsWithOverride: number;
  positionsTotal: number;
  positionsMismatched: number;
};

type MismatchRow = {
  solutionId: string;
  solutionName: string;
  accountName: string;
  tier: string;
  positionCode: string;
  expectedDiscipline: string;
  assignmentDiscipline: string | null;
  resourceId: string;
  resourceName: string;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  acknowledgementReason: string | null;
  ageDays: number;
  ageBucket: "<30d" | "30-90d" | ">90d";
};

type BenchFlexibilityReport = {
  perDiscipline: Array<{ code: string; name: string; count: number }>;
  multiDisciplineDistribution: Array<{ buckets: number; count: number }>;
  crossDisciplineAssignmentRate: Array<{ code: string; rate: number; sample: number }>;
};

function useReport<T>(endpoint: string) {
  return useQuery<T>({
    queryKey: ["report", endpoint],
    queryFn: () => jsonFetch<T>(`/api/reports/${endpoint}`),
    staleTime: 60_000,
  });
}

const BASE_REPORT_TABS = [
  { value: "pod-composition-drift", label: "Pod Composition Drift" },
  { value: "discipline-mismatch", label: "Discipline Mismatch" },
  { value: "bench-flexibility", label: "Bench Flexibility" },
  { value: "pipeline-report", label: "Pipeline Report" },
] as const;

// Every report tab is viewable by every role (product decision 2026-10-08). The RM Decision
// Log is read-only for anyone without manageDecisionLog, and each report's rows stay scoped by
// the API (an Account owner sees only their own Accounts).
const RM_DECISION_LOG_TAB = { value: "rm-decision-log", label: "RM Decision Log" } as const;

const REJECTED_PLANS_TAB = { value: "rejected-plans", label: "Rejected Plans" } as const;

type ReportTabValue =
  | (typeof BASE_REPORT_TABS)[number]["value"]
  | typeof RM_DECISION_LOG_TAB.value
  | typeof REJECTED_PLANS_TAB.value;

export function ReportingPage() {
  const reportTabs = useMemo(
    () => [...BASE_REPORT_TABS, REJECTED_PLANS_TAB, RM_DECISION_LOG_TAB],
    [],
  );

  function isVisibleTabValue(value: string): value is ReportTabValue {
    return reportTabs.some((t) => t.value === value);
  }

  const [tab, setTab] = useState<ReportTabValue>(BASE_REPORT_TABS[0].value);
  // If the role check flips the tab list (e.g. session loads after mount) and
  // the current tab is no longer visible, fall back to the first tab rather
  // than rendering a TabsContent with nothing to select it.
  const effectiveTab = isVisibleTabValue(tab)
    ? tab
    : (reportTabs[0]?.value ?? BASE_REPORT_TABS[0].value);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Reports"
        title="Staffing Reports"
        description="Live aggregates across Pod composition, Discipline coverage, and staffing drift."
      />

      <Tabs
        value={effectiveTab}
        onValueChange={(v) => {
          if (isVisibleTabValue(v)) setTab(v);
        }}
      >
        <TabsList>
          {reportTabs.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="pod-composition-drift">
          <PodCompositionDriftSection />
        </TabsContent>
        <TabsContent value="discipline-mismatch">
          <DisciplineMismatchSection />
        </TabsContent>
        <TabsContent value="bench-flexibility">
          <BenchFlexibilitySection />
        </TabsContent>
        <TabsContent value="pipeline-report">
          <PipelineReportSection />
        </TabsContent>
        <TabsContent value="rejected-plans">
          <section>
            <h2 className="font-display text-lg font-semibold">Rejected Plans</h2>
            <p className="mb-3 text-xs text-muted-foreground">
              Every time a Resource Manager rejected a plan, with who created and submitted it and
              the reason given.
            </p>
            <PlanRejectionsTable />
          </section>
        </TabsContent>
        <TabsContent value="rm-decision-log">
          <RmDecisionLogSection />
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}

function RmDecisionLogSection() {
  return (
    <section>
      <h2 className="font-display text-lg font-semibold">RM Decision Log</h2>
      <p className="mb-3 text-xs text-muted-foreground">
        Per-Solution Notes, Decisions, and Next Actions alongside Sales Stage and Resourcing Stage.
      </p>
      {/* Everyone can read it; only a holder of manageDecisionLog (the RM) can edit. */}
      <ViewOnlyGate capability="manageDecisionLog">
        <DecisionLogTable />
      </ViewOnlyGate>
    </section>
  );
}

function PipelineReportSection() {
  return (
    <section>
      <h2 className="font-display text-lg font-semibold">Pipeline Report</h2>
      <p className="mb-3 text-xs text-muted-foreground">
        Forward pipeline of Solution Demand links — Stage, Resourcing Status, Owner, and commercial
        detail per Deal.
      </p>
      <PipelineReportTable />
    </section>
  );
}

function PodCompositionDriftSection() {
  const { data, isLoading, isError } = useReport<DriftRow[]>("pod-composition-drift");
  return (
    <section>
      <h2 className="font-display text-lg font-semibold">Pod Composition Drift</h2>
      <p className="mb-3 text-xs text-muted-foreground">
        Per-tier: Solutions with Expected Discipline overrides; Positions with an active Discipline
        Mismatch.
      </p>
      {isLoading && <Skeleton lines={4} />}
      {isError && <Empty msg="Failed to load." />}
      {data && data.length === 0 && <Empty msg="No data yet." />}
      {data && data.length > 0 && (
        <div className="rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">Tier</th>
                <th className="px-3 py-2 font-medium text-right">Solutions</th>
                <th className="px-3 py-2 font-medium text-right">Override count</th>
                <th className="px-3 py-2 font-medium text-right">Override %</th>
                <th className="px-3 py-2 font-medium text-right">Positions</th>
                <th className="px-3 py-2 font-medium text-right">Mismatched</th>
                <th className="px-3 py-2 font-medium text-right">Mismatch %</th>
              </tr>
            </thead>
            <tbody>
              {data.map((r) => {
                const overridePct =
                  r.solutionsTotal === 0
                    ? 0
                    : Math.round((r.solutionsWithOverride / r.solutionsTotal) * 100);
                const mismatchPct =
                  r.positionsTotal === 0
                    ? 0
                    : Math.round((r.positionsMismatched / r.positionsTotal) * 100);
                return (
                  <tr key={r.tier} className="border-t">
                    <td className="px-3 py-2 font-medium">{r.tier}</td>
                    <td className="px-3 py-2 text-right">{r.solutionsTotal}</td>
                    <td className="px-3 py-2 text-right">{r.solutionsWithOverride}</td>
                    <td className="px-3 py-2 text-right">{overridePct}%</td>
                    <td className="px-3 py-2 text-right">{r.positionsTotal}</td>
                    <td className="px-3 py-2 text-right">{r.positionsMismatched}</td>
                    <td className="px-3 py-2 text-right">{mismatchPct}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function DisciplineMismatchSection() {
  const { data, isLoading, isError } = useReport<MismatchRow[]>("discipline-mismatches");
  return (
    <section>
      <h2 className="font-display text-lg font-semibold">Discipline Mismatch Report</h2>
      <p className="mb-3 text-xs text-muted-foreground">
        Solutions × Positions where the Assignment Discipline ≠ the Position's Expected Discipline.
      </p>
      {isLoading && <Skeleton lines={3} />}
      {isError && <Empty msg="Failed to load." />}
      {data && data.length === 0 && <Empty msg="No active Discipline mismatches. ✓" />}
      {data && data.length > 0 && (
        <div className="rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">Solution</th>
                <th className="px-3 py-2 font-medium">Account</th>
                <th className="px-3 py-2 font-medium">Tier</th>
                <th className="px-3 py-2 font-medium">Position</th>
                <th className="px-3 py-2 font-medium">Expected</th>
                <th className="px-3 py-2 font-medium">Filling as</th>
                <th className="px-3 py-2 font-medium">Resource</th>
                <th className="px-3 py-2 font-medium">Age</th>
                <th className="px-3 py-2 font-medium">Acknowledgement</th>
              </tr>
            </thead>
            <tbody>
              {data.map((r) => (
                <tr key={`${r.solutionId}-${r.positionCode}`} className="border-t align-top">
                  <td className="px-3 py-2 font-medium">{r.solutionName}</td>
                  <td className="px-3 py-2">{r.accountName}</td>
                  <td className="px-3 py-2">{r.tier}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.positionCode}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.expectedDiscipline}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.assignmentDiscipline ?? "—"}</td>
                  <td className="px-3 py-2">{r.resourceName}</td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        r.ageBucket === ">90d"
                          ? "rounded px-1.5 py-0.5 text-sm font-medium bg-[var(--fit-at-risk-bg)] text-[var(--fit-at-risk)]"
                          : r.ageBucket === "30-90d"
                            ? "rounded px-1.5 py-0.5 text-sm font-medium bg-[var(--fit-moderate-bg)] text-[var(--fit-moderate)]"
                            : "rounded px-1.5 py-0.5 text-sm font-medium bg-muted text-muted-foreground"
                      }
                    >
                      {r.ageBucket} · {r.ageDays}d
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {r.acknowledgementReason ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function BenchFlexibilitySection() {
  const { data, isLoading, isError } = useReport<BenchFlexibilityReport>("bench-flexibility");
  return (
    <section>
      <h2 className="font-display text-lg font-semibold">Bench Flexibility</h2>
      <p className="mb-3 text-xs text-muted-foreground">
        Discipline coverage of the bench, multi-discipline distribution, and cross-Discipline
        assignment rate (last 12 weeks).
      </p>
      {isLoading && <Skeleton lines={3} />}
      {isError && <Empty msg="Failed to load." />}
      {data && (
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border p-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Holders per Discipline
            </h3>
            <ul className="space-y-1.5 text-sm">
              {data.perDiscipline.map((d) => (
                <li key={d.code} className="flex items-baseline justify-between">
                  <span>
                    <span className="font-mono text-xs">{d.code}</span> · {d.name}
                  </span>
                  <span className="font-semibold">{d.count}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border p-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Multi-Discipline Distribution
            </h3>
            <ul className="space-y-1.5 text-sm">
              {data.multiDisciplineDistribution.map((d) => (
                <li key={d.buckets} className="flex items-baseline justify-between">
                  <span>
                    {d.buckets} {d.buckets === 1 ? "Discipline" : "Disciplines"}
                  </span>
                  <span className="font-semibold">{d.count}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border p-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Cross-Discipline Rate (12w)
            </h3>
            {data.crossDisciplineAssignmentRate.length === 0 ? (
              <p className="text-xs text-muted-foreground">No assignments in window.</p>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {data.crossDisciplineAssignmentRate.map((d) => (
                  <li key={d.code} className="flex items-baseline justify-between">
                    <span>
                      <span className="font-mono text-xs">{d.code}</span> · {d.sample} asgns
                    </span>
                    <span className="font-semibold">{d.rate}%</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function Skeleton({ lines }: { lines: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={String(i)} className="h-8 w-full animate-pulse rounded bg-muted/50" />
      ))}
    </div>
  );
}

function Empty({ msg }: { msg: string }) {
  return <p className="text-sm text-muted-foreground">{msg}</p>;
}
